import { differenceInDays, format, parseISO } from "date-fns";
import isNil from "lodash/isNil";

import { DateFormat } from "@/constants/DateFormat";
import { FAIconName } from "@/ui/core";
import { ColorToken } from "@/ui/theme/tokens";
import { sortDateComparator } from "@/utils/date.util";
import { getPreferredFirstName } from "@/utils/user.util";

const WeightChangeSignificanceMinimum = 0;

const isWeightChangeSignificant = (weightChange: number): boolean => {
  return Math.abs(weightChange) > WeightChangeSignificanceMinimum;
};

const getObservedAt = (data: PatientObservationData): string => {
  return format(
    parseISO(data.observation.observationDatetime),
    DateFormat.weekShortDayMonthDay,
  );
};

const getObservedAtLong = (data: PatientObservationData): string => {
  return format(
    parseISO(data.observation.observationDatetime),
    DateFormat.weekShortDayShortMonthDayTime,
  );
};

const getLoggedBy = (data: PatientObservationData): string => {
  const loggedOn = getObservedAt(data);
  if (isNil(data.createdBy)) {
    return `Logged on ${loggedOn}`;
  }
  const { chosenName, firstName } = data.createdBy;
  const loggedBy = getPreferredFirstName({ chosenName, firstName });
  return `Logged on ${loggedOn} by ${loggedBy}`;
};

const getWeightChangeIconProps = (
  weightChange?: number,
): [ColorToken, FAIconName] => {
  if (isNil(weightChange) || !isWeightChangeSignificant(weightChange)) {
    return [ColorToken.NeutralSecondary, FAIconName.Dash];
  }
  if (weightChange > 0) {
    return [ColorToken.NotificationSuccess, FAIconName.ArrowUpSolid];
  }
  return [ColorToken.NotificationCritical, FAIconName.ArrowDownSolid];
};

const getLastWeekReferenceObservation = (
  data?: PatientObservationData[],
): PatientObservationData | undefined => {
  if (isNil(data)) {
    return undefined;
  }

  const lastWeekObservations = data.filter((o) => {
    const now = new Date();
    const observedDate = parseISO(o.observation.observationDatetime);
    const diffDays = differenceInDays(observedDate, now);
    const roundedDiff = Math.round(diffDays);
    return roundedDiff >= -7 && roundedDiff <= 0;
  });

  if (lastWeekObservations.length <= 1) {
    return undefined;
  }

  return lastWeekObservations.at(-1);
};

// keep at most one decimal place
const getRoundedValue = (value: number): number => {
  return Math.round(value * 10) / 10;
};

const getPatientTrendValue = ({
  recent,
  reference,
}: {
  recent?: PatientObservationData;
  reference?: PatientObservationData;
}): number | undefined => {
  if (isNil(recent) || isNil(reference)) {
    return undefined;
  }
  const recentValue = recent.observation.value;
  const referenceValue = reference.observation.value;
  const difference = recentValue - referenceValue;
  return getRoundedValue(difference);
};

const sortObservationsByDate = ({
  data,
  isDescending = true,
}: {
  data: PatientObservationData[];
  isDescending?: boolean;
}) => {
  // eslint-disable-next-line max-params
  return data.slice().sort((a, b) => {
    return sortDateComparator({
      first: a.observation.observationDatetime,
      isDescending,
      second: b.observation.observationDatetime,
    });
  });
};

export {
  getLastWeekReferenceObservation,
  getLoggedBy,
  getObservedAt,
  getObservedAtLong,
  getPatientTrendValue,
  getRoundedValue,
  getWeightChangeIconProps,
  isWeightChangeSignificant,
  sortObservationsByDate,
};
